import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mic, Upload, Music, Clock, DollarSign, CheckCircle, 
  AlertCircle, FileAudio, Send, ArrowRight, Headphones,
  Video, Users, Star, Zap, CreditCard, Download, ExternalLink, Check, Crown
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useStripe, useElements, CardElement, Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Initialise Stripe once, synchronously, at module load. Guarded by key presence.
const STRIPE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = (STRIPE_KEY && STRIPE_KEY !== 'undefined' && STRIPE_KEY.startsWith('pk_'))
  ? loadStripe(STRIPE_KEY)
  : null;

// PayPal Logo SVG Component
const PayPalLogo = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.769.769 0 0 1 .757-.633h6.837c2.31 0 3.932.478 4.822 1.42.752.795 1.087 1.96.999 3.466-.017.288-.05.591-.1.912l-.002.013v.012c-.32 2.058-1.325 3.492-2.985 4.262-1.192.553-2.67.814-4.52.814h-.607a.769.769 0 0 0-.757.633l-.812 5.14a.641.641 0 0 1-.633.54H7.076v.008z" fill="#003087"/>
    <path d="M18.429 7.376c-.017.288-.05.591-.1.912-.895 4.656-3.957 6.262-7.874 6.262h-1.99a.969.969 0 0 0-.957.819l-1.02 6.464-.289 1.831a.51.51 0 0 0 .504.591h3.545a.85.85 0 0 0 .839-.716l.035-.179.665-4.22.043-.231a.85.85 0 0 1 .84-.717h.53c3.42 0 6.098-1.39 6.881-5.412.327-1.68.158-3.084-.707-4.071a3.389 3.389 0 0 0-.945-.733z" fill="#009cde"/>
  </svg>
);

// Payment Form Component - Card + PayPal options
const PaymentForm = ({ project, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('card'); // 'card' or 'paypal'

  const handlePayPal = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const res = await fetch(`${API}/api/recording-studio/projects/${project.id}/create-paypal-checkout`, {
        method: 'POST'
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to create PayPal checkout');
      }
      
      const { checkout_url } = data;
      window.location.href = checkout_url;
    } catch (err) {
      console.error('PayPal error:', err);
      setError(err.message || 'PayPal checkout failed');
      toast.error(err.message || 'PayPal checkout failed');
      setLoading(false);
    }
  };

  const handleCardPayment = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) return;

    setLoading(true);
    setError(null);

    try {
      // Create payment intent
      const res = await fetch(`${API}/api/recording-studio/projects/${project.id}/create-payment`, {
        method: 'POST'
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to create payment');
      }
      
      const { client_secret } = data;

      // Confirm payment
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(client_secret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: project.client_name,
            email: project.client_email
          }
        }
      });

      if (stripeError) {
        setError(stripeError.message);
        toast.error(stripeError.message);
      } else if (paymentIntent.status === 'succeeded') {
        // Confirm payment on backend
        const confirmRes = await fetch(`${API}/api/recording-studio/projects/${project.id}/confirm-payment?payment_intent_id=${paymentIntent.id}`, {
          method: 'POST'
        });
        
        if (confirmRes.ok) {
          toast.success('Payment successful!');
          onSuccess();
        } else {
          console.warn('Backend confirm failed but payment succeeded');
          toast.success('Payment successful!');
          onSuccess();
        }
      } else if (paymentIntent.status === 'requires_action') {
        setError('Additional authentication required. Please complete the verification.');
      } else {
        setError(`Payment status: ${paymentIntent.status}. Please try again.`);
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'Payment failed. Please try again.');
      toast.error(err.message || 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (paymentMethod === 'paypal') {
      handlePayPal();
    } else {
      handleCardPayment(e);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Payment Method Selector */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <button
          type="button"
          onClick={() => setPaymentMethod('card')}
          className={`relative p-4 rounded-xl border-2 transition-all text-left ${
            paymentMethod === 'card'
              ? 'border-green-500 bg-green-500/10'
              : 'border-zinc-700 hover:border-zinc-500 bg-zinc-800'
          }`}
        >
          {paymentMethod === 'card' && (
            <div className="absolute top-2 right-2 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
              <Check className="w-3 h-3 text-black" />
            </div>
          )}
          <div className="flex items-center gap-2 mb-1">
            <CreditCard className="w-5 h-5" />
            <span className="font-bold">Card</span>
          </div>
          <p className="text-xs text-zinc-400">
            Visa, Mastercard, Amex
          </p>
        </button>
        
        <button
          type="button"
          onClick={() => setPaymentMethod('paypal')}
          className={`relative p-4 rounded-xl border-2 transition-all text-left ${
            paymentMethod === 'paypal'
              ? 'border-green-500 bg-green-500/10'
              : 'border-zinc-700 hover:border-zinc-500 bg-zinc-800'
          }`}
        >
          {paymentMethod === 'paypal' && (
            <div className="absolute top-2 right-2 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
              <Check className="w-3 h-3 text-black" />
            </div>
          )}
          <div className="flex items-center gap-2 mb-1">
            <PayPalLogo className="w-5 h-5" />
            <span className="font-bold">PayPal</span>
          </div>
          <p className="text-xs text-zinc-400">
            Pay with PayPal
          </p>
        </button>
      </div>
      
      {/* Card Input - only show for card payment */}
      {paymentMethod === 'card' && (
        <div className="bg-zinc-800 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3 text-sm text-zinc-400">
            <CreditCard className="w-4 h-4" />
            <span>Accepts Visa, Mastercard, Amex • Apple Pay • Google Pay</span>
          </div>
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#fff',
                  '::placeholder': { color: '#666' }
                }
              }
            }}
          />
        </div>
      )}
      
      {/* PayPal notice */}
      {paymentMethod === 'paypal' && (
        <div className="bg-zinc-800 rounded-lg p-4 text-center">
          <PayPalLogo className="w-24 h-8 mx-auto mb-2" />
          <p className="text-sm text-zinc-400">
            You'll be redirected to PayPal to complete your payment
          </p>
        </div>
      )}
      
      {error && (
        <div className="text-red-400 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}
      
      <button
        type="submit"
        data-testid="pay-button"
        disabled={(paymentMethod === 'card' && !stripe) || loading}
        className="w-full py-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 text-black font-bold rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {loading ? (
          <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
        ) : (
          <>
            {paymentMethod === 'paypal' ? (
              <PayPalLogo className="w-5 h-5" />
            ) : (
              <CreditCard className="w-5 h-5" />
            )}
            Pay ${project?.price} {paymentMethod === 'paypal' ? 'with PayPal' : ''}
          </>
        )}
      </button>
    </form>
  );
};

import AdminFreeAccessBar, { generateStudioRoomCode } from '../components/admin/AdminFreeAccessBar';
import { AdminEditorLink } from '@/components/edit/AdminEditorLink';

const RecordingStudio = () => {
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();
  const isAdminUser = typeof isAdmin === 'function' ? isAdmin() : false;
  const [tiers, setTiers] = useState([]);
  const [selectedTier, setSelectedTier] = useState(null);
  const [step, setStep] = useState(1); // 1: Select tier, 2: Project details, 3: Upload, 4: Confirm & Pay, 5: Success
  const [loading, setLoading] = useState(false);
  const [projectData, setProjectData] = useState({
    client_name: '',
    client_email: '',
    project_name: '',
    description: '',
    genre: 'Brasilian',
    tempo: '',
    time_signature: '4/4',
    notes: ''
  });
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [createdProject, setCreatedProject] = useState(null);
  const [isPaid, setIsPaid] = useState(false);

  useEffect(() => {
    fetchTiers();
    
    // Check URL params for payment return
    const urlParams = new URLSearchParams(window.location.search);
    const projectId = urlParams.get('project');
    const paymentStatus = urlParams.get('payment');
    const paypalToken = urlParams.get('token'); // PayPal adds this on return
    
    if (projectId && paymentStatus === 'success') {
      // Stripe payment success - confirm on backend
      confirmCheckoutPayment(projectId);
    } else if (projectId && paymentStatus === 'paypal_success' && paypalToken) {
      // PayPal payment success - capture the payment
      capturePayPalPayment(projectId, paypalToken);
    } else if (projectId && paymentStatus === 'cancelled') {
      toast.info('Payment was cancelled. You can try again.');
      loadProject(projectId);
    }
  }, []);
  
  const capturePayPalPayment = async (projectId, token) => {
    try {
      const res = await fetch(`${API}/api/recording-studio/projects/${projectId}/capture-paypal?order_id=${token}`, {
        method: 'POST'
      });
      
      if (res.ok) {
        toast.success('PayPal payment successful! Your project is now in progress.');
        setIsPaid(true);
        setStep(5);
        window.history.replaceState({}, '', window.location.pathname);
      } else {
        // Response not ok - try to get error details
        try {
          const errData = await res.json();
          toast.error(errData.detail || 'Could not complete PayPal payment. Please contact support.');
        } catch {
          toast.error('Could not complete PayPal payment. Please contact support.');
        }
      }
    } catch (err) {
      console.error('Error capturing PayPal payment:', err);
      toast.error('Payment error. Please contact support.');
    }
  };
  
  const confirmCheckoutPayment = async (projectId) => {
    try {
      const res = await fetch(`${API}/api/recording-studio/projects/${projectId}/confirm-payment`, {
        method: 'POST'
      });
      
      if (res.ok) {
        toast.success('Payment successful! Your project is now in progress.');
        setIsPaid(true);
        setStep(5); // Go to success step
        // Clear URL params
        window.history.replaceState({}, '', window.location.pathname);
      } else {
        toast.error('Could not confirm payment. Please contact support.');
      }
    } catch (err) {
      console.error('Error confirming payment:', err);
    }
  };
  
  const loadProject = async (projectId) => {
    try {
      const res = await fetch(`${API}/api/recording-studio/projects/${projectId}`);
      if (res.ok) {
        const data = await res.json();
        setCreatedProject(data.project);
        setStep(4); // Go to payment step
      }
    } catch (err) {
      console.error('Error loading project:', err);
    }
  };

  const fetchTiers = async () => {
    try {
      // Send admin JWT when present so the backend returns the admin-only test tier too.
      const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API}/api/recording-studio/tiers`, { headers });
      if (!res.ok) {
        throw new Error('Failed to fetch tiers');
      }
      const data = await res.json();
      setTiers(data.tiers || []);
    } catch (err) {
      console.error('Error fetching tiers:', err);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setUploadedFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!selectedTier) return;
    
    setLoading(true);
    try {
      // Create project
      const res = await fetch(`${API}/api/recording-studio/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...projectData,
          tempo: projectData.tempo ? parseInt(projectData.tempo) : null,
          service_tier: selectedTier.id
        })
      });
      
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to create project');
      
      const project = data.project;
      setCreatedProject(project);
      
      // Upload files if any
      for (const file of uploadedFiles) {
        const formData = new FormData();
        formData.append('file', file);
        
        await fetch(`${API}/api/recording-studio/projects/${project.id}/upload-track`, {
          method: 'POST',
          body: formData
        });
      }
      
      toast.success('Project submitted successfully!');
      setStep(4);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const tierIcons = {
    test: <Zap className="w-8 h-8" />,
    demo: <Mic className="w-8 h-8" />,
    standard: <Headphones className="w-8 h-8" />,
    premium: <Star className="w-8 h-8" />,
    live_session: <Video className="w-8 h-8" />
  };

  const tierColors = {
    test: 'from-red-500 to-pink-500',
    demo: 'from-blue-500 to-cyan-500',
    standard: 'from-purple-500 to-pink-500',
    premium: 'from-amber-500 to-orange-500',
    live_session: 'from-green-500 to-emerald-500'
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <AdminFreeAccessBar
        title="Admin — Recording Studio Free Access"
        subtitle="Skip all forms and payment. Enter a live studio room right now."
        testid="admin-bar-recording-studio"
        primary={{
          label: 'Enter Studio Now',
          icon: Video,
          onClick: () => {
            const code = generateStudioRoomCode();
            window.location.href = `/video-room/${code}/host`;
          },
        }}
        secondary={{
          label: 'Open DAW',
          onClick: () => { window.location.href = '/admin/studio-session'; },
        }}
      />
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-transparent to-amber-900/20" />
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-4 bg-gradient-to-r from-amber-400 via-orange-500 to-red-500 bg-clip-text text-transparent">
              Online Recording Studio
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Professional Brasilian percussion recording services. 
              From quick demos to full productions, delivered worldwide.
            </p>
          </motion.div>

          {/* Admin Quick Access - Skip all forms */}
          {isAdminUser && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-3xl mx-auto mb-10 p-5 rounded-2xl border-2 border-yellow-500/50 bg-gradient-to-r from-yellow-500/10 to-amber-500/10"
              data-testid="admin-quick-access-banner"
            >
              <div className="flex flex-col md:flex-row items-start md:items-center gap-4 justify-between">
                <div className="flex items-start gap-3">
                  <Crown className="w-6 h-6 text-yellow-400 shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-yellow-300 text-lg">Admin — Skip Everything</h3>
                    <p className="text-sm text-yellow-100/80 mt-1">
                      You're logged in as admin. No need to fill client details or pay. Jump straight into the studio.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  data-testid="admin-go-to-studio-btn"
                  onClick={() => navigate('/admin/studio-session')}
                  className="shrink-0 px-6 py-3 rounded-xl font-bold bg-yellow-500 hover:bg-yellow-400 text-black transition-colors flex items-center gap-2 whitespace-nowrap"
                >
                  <Headphones className="w-5 h-5" /> Go to Studio Now
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </motion.div>
          )}

          {/* Progress Steps */}
          <div className="flex justify-center mb-12">
            <div className="flex items-center gap-4">
              {[
                { num: 1, label: 'Select Service' },
                { num: 2, label: 'Project Details' },
                { num: 3, label: 'Upload Tracks' },
                { num: 4, label: 'Confirm' }
              ].map((s, i) => (
                <React.Fragment key={s.num}>
                  <div 
                    className={`flex items-center gap-2 ${step >= s.num ? 'text-amber-400' : 'text-gray-600'}`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      step >= s.num ? 'bg-amber-500 text-black' : 'bg-gray-800 text-gray-500'
                    }`}>
                      {step > s.num ? <CheckCircle className="w-5 h-5" /> : s.num}
                    </div>
                    <span className="hidden md:inline text-sm">{s.label}</span>
                  </div>
                  {i < 3 && <ArrowRight className={`w-4 h-4 ${step > s.num ? 'text-amber-400' : 'text-gray-700'}`} />}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Step Content */}
          <AnimatePresence mode="wait">
            {/* Step 1: Select Service Tier */}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
              >
                {tiers.map(tier => (
                  <motion.div
                    key={tier.id}
                    data-testid={`tier-${tier.id}`}
                    whileHover={{ scale: 1.02, y: -5 }}
                    onClick={() => setSelectedTier(tier)}
                    className={`relative bg-[#141414] rounded-2xl p-6 cursor-pointer border-2 transition-all ${
                      selectedTier?.id === tier.id 
                        ? 'border-amber-500 shadow-lg shadow-amber-500/20' 
                        : 'border-transparent hover:border-gray-700'
                    }`}
                  >
                    <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${tierColors[tier.id]} flex items-center justify-center mb-4`}>
                      {tierIcons[tier.id]}
                    </div>
                    
                    <h3 className="text-xl font-bold mb-2">{tier.name}</h3>
                    <p className="text-gray-400 text-sm mb-4">{tier.description}</p>
                    
                    <div className="flex items-baseline gap-1 mb-4">
                      <span className="text-3xl font-bold text-amber-400">${tier.price}</span>
                      {tier.id === 'live_session' && <span className="text-gray-500">/hour</span>}
                      {isAdminUser && (
                        <span className="ml-2 text-xs font-semibold text-yellow-400 bg-yellow-400/10 px-2 py-0.5 rounded">
                          Admin — free checkout
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                      <Clock className="w-4 h-4" />
                      <span>{tier.turnaround_days === 0 ? 'Live Session' : `${tier.turnaround_days} day turnaround`}</span>
                    </div>
                    
                    <ul className="space-y-2">
                      {tier.includes.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                          <CheckCircle className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    
                    {selectedTier?.id === tier.id && (
                      <div className="absolute top-4 right-4">
                        <CheckCircle className="w-6 h-6 text-amber-500" />
                      </div>
                    )}
                  </motion.div>
                ))}
              </motion.div>
            )}

            {/* Step 2: Project Details */}
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="max-w-2xl mx-auto"
              >
                <div className="bg-[#141414] rounded-2xl p-8">
                  <h2 className="text-2xl font-bold mb-6">Project Details</h2>
                  
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Your Name *</label>
                      <input
                        type="text"
                        data-testid="client-name-input"
                        value={projectData.client_name}
                        onChange={e => setProjectData(p => ({ ...p, client_name: e.target.value }))}
                        className="w-full bg-[#0a0a0a] border border-gray-700 rounded-lg px-4 py-3 focus:border-amber-500 focus:outline-none"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Email *</label>
                      <input
                        type="email"
                        data-testid="client-email-input"
                        value={projectData.client_email}
                        onChange={e => setProjectData(p => ({ ...p, client_email: e.target.value }))}
                        className="w-full bg-[#0a0a0a] border border-gray-700 rounded-lg px-4 py-3 focus:border-amber-500 focus:outline-none"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm text-gray-400 mb-2">Project Name *</label>
                    <input
                      type="text"
                      data-testid="project-name-input"
                      value={projectData.project_name}
                      onChange={e => setProjectData(p => ({ ...p, project_name: e.target.value }))}
                      className="w-full bg-[#0a0a0a] border border-gray-700 rounded-lg px-4 py-3 focus:border-amber-500 focus:outline-none"
                      placeholder="My Samba Track"
                    />
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm text-gray-400 mb-2">Project Description *</label>
                    <textarea
                      data-testid="project-description-input"
                      value={projectData.description}
                      onChange={e => setProjectData(p => ({ ...p, description: e.target.value }))}
                      rows={4}
                      className="w-full bg-[#0a0a0a] border border-gray-700 rounded-lg px-4 py-3 focus:border-amber-500 focus:outline-none resize-none"
                      placeholder="Describe what you're looking for - style, instruments, feel, references..."
                    />
                  </div>
                  
                  <div className="grid md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Genre</label>
                      <select
                        value={projectData.genre}
                        onChange={e => setProjectData(p => ({ ...p, genre: e.target.value }))}
                        className="w-full bg-[#0a0a0a] border border-gray-700 rounded-lg px-4 py-3 focus:border-amber-500 focus:outline-none"
                      >
                        <option value="Brasilian">Brasilian</option>
                        <option value="Samba">Samba</option>
                        <option value="Bossa Nova">Bossa Nova</option>
                        <option value="Forró">Forró</option>
                        <option value="MPB">MPB</option>
                        <option value="Afro-Brasilian">Afro-Brasilian</option>
                        <option value="Latin Jazz">Latin Jazz</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Tempo (BPM)</label>
                      <input
                        type="number"
                        value={projectData.tempo}
                        onChange={e => setProjectData(p => ({ ...p, tempo: e.target.value }))}
                        className="w-full bg-[#0a0a0a] border border-gray-700 rounded-lg px-4 py-3 focus:border-amber-500 focus:outline-none"
                        placeholder="120"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Time Signature</label>
                      <select
                        value={projectData.time_signature}
                        onChange={e => setProjectData(p => ({ ...p, time_signature: e.target.value }))}
                        className="w-full bg-[#0a0a0a] border border-gray-700 rounded-lg px-4 py-3 focus:border-amber-500 focus:outline-none"
                      >
                        <option value="4/4">4/4</option>
                        <option value="3/4">3/4</option>
                        <option value="6/8">6/8</option>
                        <option value="2/4">2/4</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm text-gray-400 mb-2">Additional Notes</label>
                    <textarea
                      value={projectData.notes}
                      onChange={e => setProjectData(p => ({ ...p, notes: e.target.value }))}
                      rows={3}
                      className="w-full bg-[#0a0a0a] border border-gray-700 rounded-lg px-4 py-3 focus:border-amber-500 focus:outline-none resize-none"
                      placeholder="Any specific requests, reference tracks, or preferences..."
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 3: Upload Tracks */}
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="max-w-2xl mx-auto"
              >
                <div className="bg-[#141414] rounded-2xl p-8">
                  <h2 className="text-2xl font-bold mb-2">Upload Your Tracks</h2>
                  <p className="text-gray-400 mb-6">
                    Upload the audio files you want me to add percussion to. 
                    Supported formats: WAV, MP3, AIFF, FLAC
                  </p>
                  
                  <div 
                    className="border-2 border-dashed border-gray-700 rounded-xl p-8 text-center hover:border-amber-500 transition-colors cursor-pointer"
                    onClick={() => document.getElementById('file-upload').click()}
                  >
                    <input
                      id="file-upload"
                      type="file"
                      multiple
                      accept="audio/*"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <Upload className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                    <p className="text-gray-400 mb-2">Drag & drop your audio files here</p>
                    <p className="text-sm text-gray-600">or click to browse</p>
                  </div>
                  
                  {uploadedFiles.length > 0 && (
                    <div className="mt-6 space-y-3">
                      <h3 className="text-sm font-semibold text-gray-400">Uploaded Files:</h3>
                      {uploadedFiles.map((file, i) => (
                        <div key={i} className="flex items-center justify-between bg-[#0a0a0a] rounded-lg px-4 py-3">
                          <div className="flex items-center gap-3">
                            <FileAudio className="w-5 h-5 text-amber-500" />
                            <span className="text-sm">{file.name}</span>
                            <span className="text-xs text-gray-500">
                              ({(file.size / (1024 * 1024)).toFixed(2)} MB)
                            </span>
                          </div>
                          <button
                            onClick={() => removeFile(i)}
                            className="text-red-400 hover:text-red-300 text-sm"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                      <div className="text-sm text-amber-200">
                        <p className="font-semibold mb-1">Tips for best results:</p>
                        <ul className="list-disc list-inside text-amber-200/80 space-y-1">
                          <li>Export at 44.1kHz or 48kHz sample rate</li>
                          <li>Include a click track if available</li>
                          <li>Mark any sections with specific percussion needs</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 4: Confirmation & Payment */}
            {step === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="max-w-2xl mx-auto"
              >
                <div className="bg-[#141414] rounded-2xl p-8">
                  {!isPaid ? (
                    <>
                      <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CreditCard className="w-8 h-8 text-amber-400" />
                      </div>
                      
                      <h2 className="text-2xl font-bold mb-2 text-center">Complete Your Payment</h2>
                      <p className="text-gray-400 mb-6 text-center">
                        Secure payment to start your project
                      </p>
                      
                      {createdProject && (
                        <div className="bg-[#0a0a0a] rounded-xl p-6 mb-6">
                          <h3 className="text-lg font-semibold mb-4">Project Summary</h3>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500">Project:</span>
                              <span className="ml-2 text-white">{createdProject.project_name}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Service:</span>
                              <span className="ml-2 text-amber-400">{selectedTier?.name}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Files:</span>
                              <span className="ml-2 text-white">{uploadedFiles.length} track(s)</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Total:</span>
                              <span className="ml-2 text-green-400 text-lg font-bold">${createdProject.price}</span>
                            </div>
                          </div>
                          
                          {/* Deliverables included */}
                          <div className="mt-4 pt-4 border-t border-zinc-800">
                            <h4 className="text-sm font-medium text-gray-400 mb-2">Includes:</h4>
                            <ul className="text-sm text-gray-300 space-y-1">
                              {selectedTier?.includes?.map((item, i) => (
                                <li key={i} className="flex items-center gap-2">
                                  <CheckCircle className="w-3 h-3 text-green-400" />
                                  {item}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}
                      
                      {createdProject && !isAdminUser && (
                        <Elements stripe={stripePromise}>
                          <PaymentForm 
                            project={createdProject} 
                            onSuccess={() => {
                              setIsPaid(true);
                              setStep(5);
                            }} 
                          />
                        </Elements>
                      )}

                      {createdProject && isAdminUser && (
                        <div className="p-5 rounded-2xl border-2 border-yellow-500/50 bg-gradient-to-r from-yellow-500/10 to-amber-500/10">
                          <div className="flex items-start gap-3 mb-4">
                            <Crown className="w-6 h-6 text-yellow-400 shrink-0 mt-1" />
                            <div>
                              <h3 className="font-bold text-yellow-300 text-lg">Admin Trial Access</h3>
                              <p className="text-sm text-yellow-100/80 mt-1">
                                Skip Stripe/PayPal and mark this project as paid to verify the studio delivers files, confirmation email, and project dashboard end-to-end. No charge.
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                await fetch(`${API}/api/recording-studio/projects/${createdProject.id}/admin-mark-paid`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ admin_email: user?.email || 'admin@casasamba.com' }),
                                }).catch(() => null);
                                setIsPaid(true);
                                setStep(5);
                                toast.success('Admin trial — project marked paid');
                              } catch (err) {
                                setIsPaid(true);
                                setStep(5);
                                toast.success('Admin trial — proceeding without payment');
                              }
                            }}
                            data-testid="admin-trial-mark-paid-btn"
                            className="w-full py-3 rounded-xl font-bold bg-yellow-500 hover:bg-yellow-400 text-black transition-colors flex items-center justify-center gap-2"
                          >
                            <CheckCircle className="w-5 h-5" /> Mark Paid & Continue (Free)
                          </button>
                        </div>
                      )}
                      
                      <p className="text-xs text-gray-500 text-center mt-4">
                        🔒 Secure payment powered by Stripe
                      </p>
                    </>
                  ) : (
                    <div className="text-center">
                      <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="w-10 h-10 text-green-500" />
                      </div>
                      <h2 className="text-2xl font-bold mb-2">Payment Successful!</h2>
                      <p className="text-gray-400">Redirecting to your project...</p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Step 5: Success & Next Steps */}
            {step === 5 && (
              <motion.div
                key="step5"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="max-w-2xl mx-auto text-center"
              >
                <div className="bg-[#141414] rounded-2xl p-8">
                  <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-green-500" />
                  </div>
                  
                  <h2 className="text-3xl font-bold mb-4">You're All Set!</h2>
                  <p className="text-gray-400 mb-6">
                    {selectedTier?.id === 'live_session' 
                      ? "Your live session is ready! Click below to enter the studio."
                      : `Your project is now in progress. I'll have your tracks ready within ${selectedTier?.turnaround_days || 5} days.`
                    }
                  </p>
                  
                  {/* Live Session - Direct Studio Access */}
                  {selectedTier?.id === 'live_session' && createdProject && (
                    <div className="mb-8">
                      <Link
                        to={`/admin/studio-session?project=${createdProject.id}`}
                        className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 text-black font-bold text-lg rounded-xl transition-all"
                      >
                        <Headphones className="w-6 h-6" />
                        Enter Studio Now
                        <ArrowRight className="w-5 h-5" />
                      </Link>
                      <p className="text-gray-500 text-sm mt-3">Your studio session is ready and waiting</p>
                    </div>
                  )}
                  
                  {createdProject && (
                    <div className="bg-[#0a0a0a] rounded-xl p-6 mb-6 text-left">
                      <h3 className="text-lg font-semibold mb-4">What's Next?</h3>
                      <div className="space-y-3">
                        <div className="flex items-start gap-3">
                          <div className="w-6 h-6 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-amber-400 text-xs font-bold">1</span>
                          </div>
                          <div>
                            <p className="text-white font-medium">I'll review your tracks</p>
                            <p className="text-gray-500 text-sm">Listening to your submission and planning the percussion</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="w-6 h-6 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-amber-400 text-xs font-bold">2</span>
                          </div>
                          <div>
                            <p className="text-white font-medium">Recording & Production</p>
                            <p className="text-gray-500 text-sm">Adding authentic Afro-Brasilian percussion to your music</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="w-6 h-6 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-amber-400 text-xs font-bold">3</span>
                          </div>
                          <div>
                            <p className="text-white font-medium">Delivery</p>
                            <p className="text-gray-500 text-sm">You'll receive an email with download links for your finished tracks</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6">
                    <p className="text-amber-400 text-sm">
                      📧 A confirmation email has been sent to <strong>{projectData.client_email}</strong>
                    </p>
                  </div>
                  
                  <div className="flex gap-4 justify-center">
                    <Link
                      to="/"
                      className="px-6 py-3 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      Back to Home
                    </Link>
                    <Link
                      to={`/my-studio-projects?email=${encodeURIComponent(projectData.client_email)}`}
                      className="px-6 py-3 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-lg transition-colors flex items-center gap-2"
                    >
                      View My Projects
                      <ExternalLink className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Navigation Buttons */}
          {step < 4 && (
            <div className="flex justify-center gap-4 mt-8">
              {step > 1 && (
                <button
                  onClick={() => setStep(s => s - 1)}
                  className="px-6 py-3 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Back
                </button>
              )}
              
              <button
                data-testid="continue-btn"
                onClick={() => {
                  if (step === 1 && !selectedTier) {
                    toast.error('Please select a service tier');
                    return;
                  }
                  if (step === 2) {
                    if (!projectData.client_name || !projectData.client_email || 
                        !projectData.project_name || !projectData.description) {
                      toast.error('Please fill in all required fields');
                      return;
                    }
                  }
                  if (step === 3) {
                    handleSubmit();
                    return;
                  }
                  setStep(s => s + 1);
                }}
                disabled={loading}
                className="px-8 py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-black font-semibold rounded-lg transition-all flex items-center gap-2 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    Submitting...
                  </>
                ) : step === 3 ? (
                  <>
                    <Send className="w-5 h-5" />
                    Submit Project
                  </>
                ) : (
                  <>
                    Continue
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold text-center mb-12">Why Choose Our Studio?</h2>
        
        <div className="grid md:grid-cols-3 gap-8">
          {[
            {
              icon: <Headphones className="w-8 h-8" />,
              title: "Professional Quality",
              description: "Studio-grade recordings with authentic Brasilian percussion instruments"
            },
            {
              icon: <Zap className="w-8 h-8" />,
              title: "Fast Turnaround",
              description: "Quick delivery without compromising quality - demos in 3 days"
            },
            {
              icon: <Users className="w-8 h-8" />,
              title: "Direct Communication",
              description: "Work directly with the artist - no middlemen, clear feedback"
            }
          ].map((feature, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="bg-[#141414] rounded-xl p-6 text-center"
            >
              <div className="w-16 h-16 bg-amber-500/20 rounded-xl flex items-center justify-center mx-auto mb-4 text-amber-500">
                {feature.icon}
              </div>
              <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
              <p className="text-gray-400">{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Sample Store Promo Section */}
      <div className="bg-gradient-to-br from-purple-900/30 via-[#0a0a0a] to-amber-900/20 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="bg-[#141414] rounded-2xl p-8 md:p-12 border border-purple-500/30 relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-amber-500/10" />
            <div className="relative flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="text-center md:text-left">
                <span className="inline-block px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-sm font-semibold mb-4">
                  INSTANT DOWNLOAD
                </span>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Need Samples <span className="text-amber-400">Right Now?</span>
                </h2>
                <p className="text-gray-400 max-w-lg mb-6">
                  Browse our collection of authentic Brasilian drum samples. 
                  One-shots, loops, full tracks, and stem packs - download instantly and start creating!
                </p>
                <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                  <span className="px-3 py-1 bg-[#0a0a0a] rounded-full text-sm text-gray-400">One Shots from $0.99</span>
                  <span className="px-3 py-1 bg-[#0a0a0a] rounded-full text-sm text-gray-400">Loops from $2.99</span>
                  <span className="px-3 py-1 bg-[#0a0a0a] rounded-full text-sm text-gray-400">Full Tracks from $24.99</span>
                </div>
              </div>
              <div className="flex flex-col items-center gap-4">
                <Link
                  to="/sample-store"
                  className="flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-purple-500 to-amber-500 hover:from-purple-600 hover:to-amber-600 text-white font-bold rounded-xl transition-all shadow-lg shadow-purple-500/30 text-lg"
                >
                  <Music className="w-6 h-6" />
                  Browse Sample Store
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <span className="text-sm text-gray-500">25+ samples available</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      <AdminEditorLink to="/admin/dashboard#recording-studio-btn" label="Edit Recording Studio" />
    </div>
  );
};

export default RecordingStudio;
